import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import {
  Award, Star, BadgeCheck, Users, ArrowRight, Stethoscope, Search, ShieldCheck, Clock,
} from "lucide-react";

// ---------------------------------------------------------------------------
// /doctors — full listing page for verified specialists. Referenced from
// Navbar ("Meet Specialists") and Landing "Browse all specialists" CTA.
// Reuses the same card visual as DoctorsPreview on the landing page.
// ---------------------------------------------------------------------------
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoOf = (d) =>
  d?.photo_url ? (d.photo_url.startsWith("http") ? d.photo_url : `${BACKEND}${d.photo_url}`) : null;

export default function Doctors() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Verified Neurologists & Neurosurgeons · NeuroOpinion India";
    api.get("/doctors")
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return docs;
    return docs.filter((d) =>
      [d.name, d.specialization, d.alma_mater, d.bio]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(term))
    );
  }, [q, docs]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-semibold tracking-wide">
              <BadgeCheck className="w-3.5 h-3.5" /> MCI / NMC verified
            </div>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Verified <span className="text-teal-600">Neurologists &amp; Neurosurgeons</span>
            </h1>
            <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
              Every specialist on our network is a NIMHANS, AIIMS or premier-institute alumnus with
              10+ years of practice. Registration and credentials are verified before activation.
            </p>
            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-teal-600"/>MCI/NMC checked</span>
              <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-teal-600"/>10+ yrs experience</span>
              <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-500 fill-amber-500"/>4.9 avg rating</span>
            </div>
          </div>

          {/* Search */}
          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, specialty, alma mater…"
              className="pl-9 h-11 rounded-full bg-white border-slate-200"
              data-testid="doctors-search-input"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="p-6 border-slate-200 bg-white rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2" />
                    <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
                  </div>
                </div>
                <div className="mt-4 h-3 bg-slate-100 rounded animate-pulse w-full" />
                <div className="mt-2 h-3 bg-slate-100 rounded animate-pulse w-5/6" />
              </Card>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Stethoscope className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-600 text-sm">
              {q ? "No specialists match your search." : "No specialists available right now."}
            </p>
          </div>
        ) : (
          <>
            {/* Featured founder hero — appears when a featured doctor exists
                in the list (find, not assume position 0). Skipped on active
                search so the search results stay clean and predictable. */}
            {!q && (() => {
              const hero = filtered.find((x) => x.is_featured);
              const rest = hero ? filtered.filter((x) => x.id !== hero.id) : filtered;
              return (
                <>
                  {hero && <FeaturedHero doc={hero} />}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((d) => renderCard(d))}
                  </div>
                </>
              );
            })()}
            {q && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((d) => renderCard(d))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );

  function renderCard(d) {
    return (
      <Link key={d.id} to={`/doctors/${d.id}`} data-testid={`doctors-card-${d.id}`}>
                <Card className="relative p-6 border-slate-200 bg-white hover:shadow-lg hover:border-teal-300 hover:-translate-y-0.5 transition-all duration-200 h-full overflow-hidden rounded-2xl group">
                  {d.is_available !== false && (
                    <span
                      className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700"
                      data-testid={`doctor-live-${d.id}`}
                      title="Available for new cases today"
                    >
                      <span className="relative flex w-1.5 h-1.5">
                        <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-75 animate-ping"/>
                        <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-500"/>
                      </span>
                      Live
                    </span>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="relative shrink-0">
                      {photoOf(d) ? (
                        <img
                          src={photoOf(d)}
                          alt={d.name}
                          className="w-20 h-20 rounded-2xl object-cover object-top ring-2 ring-teal-100 bg-slate-100"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 text-white grid place-items-center">
                          <Stethoscope className="w-10 h-10" />
                        </div>
                      )}
                      <span
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-white grid place-items-center"
                        title="MCI/NMC verified"
                      >
                        <BadgeCheck className="w-3 h-3 text-white" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-600 truncate">{d.specialization}</div>
                      {d.alma_mater && (
                        <div className="text-[11px] text-teal-700 font-semibold mt-0.5 truncate">
                          {d.alma_mater} alumnus
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Award className="w-3.5 h-3.5 text-teal-600" />
                          {d.experience_years} yrs
                        </span>
                        {d.rating > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                            <Star className="w-3.5 h-3.5 fill-amber-500" />
                            {Number(d.rating).toFixed(1)}
                          </span>
                        )}
                        {d.median_response_hrs > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-emerald-700 font-semibold"
                            title="Typical reply time — last 30 days"
                            data-testid={`doctor-reply-${d.id}`}
                          >
                            <Clock className="w-3.5 h-3.5"/>~{d.median_response_hrs}h reply
                          </span>
                        )}
                        {typeof d.consultations_count === "number" && d.consultations_count > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {d.consultations_count >= 1000
                              ? `${(d.consultations_count / 1000).toFixed(d.consultations_count >= 10000 ? 0 : 1)}K+`
                              : d.consultations_count.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {d.bio && <p className="mt-4 text-sm text-slate-600 line-clamp-3">{d.bio}</p>}
                  <div className="mt-5 flex items-center justify-end pt-4 border-t border-slate-100">
                    <span className="text-sm font-semibold text-teal-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      View profile <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Card>
              </Link>
    );
  }
}

// ---------------------------------------------------------------------------
// FeaturedHero — large gradient card for the founder / anchor specialist.
// Shown at the top of the /doctors listing when the first doc has
// is_featured=true. Not shown during search to keep results predictable.
// ---------------------------------------------------------------------------
function FeaturedHero({ doc }) {
  const photo = doc?.photo_url
    ? (doc.photo_url.startsWith("http") ? doc.photo_url : `${BACKEND}${doc.photo_url}`)
    : null;
  return (
    <Link to={`/doctors/${doc.id}`} data-testid={`doctors-featured-${doc.id}`}>
      <div className="relative mb-10 overflow-hidden rounded-3xl bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700 text-white shadow-xl hover:shadow-2xl transition-shadow duration-300">
        {/* Decorative grain / gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-teal-900/40 via-transparent to-transparent"/>
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl"/>
        <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-teal-400/10 blur-3xl"/>

        <div className="relative grid grid-cols-1 md:grid-cols-[240px_1fr] lg:grid-cols-[300px_1fr] gap-6 md:gap-10 p-6 md:p-10">
          {/* Photo */}
          <div className="relative flex justify-center md:justify-start">
            {photo ? (
              <img
                src={photo}
                alt={doc.name}
                className="w-40 h-40 md:w-full md:h-64 lg:h-72 rounded-3xl object-cover object-top ring-4 ring-teal-400/30 shadow-2xl"
              />
            ) : (
              <div className="w-40 h-40 md:w-full md:h-64 rounded-3xl bg-teal-500/30 grid place-items-center">
                <Stethoscope className="w-20 h-20 text-teal-200"/>
              </div>
            )}
            {doc.is_available !== false && (
              <span
                className="absolute -top-2 -right-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg"
                title="Accepting new cases today"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-white opacity-75 animate-ping"/>
                  <span className="relative rounded-full w-1.5 h-1.5 bg-white"/>
                </span>
                Live now
              </span>
            )}
          </div>

          {/* Copy */}
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-100 text-[10px] font-bold uppercase tracking-wider">
                <Award className="w-3 h-3"/> Founder &amp; anchor specialist
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-300/40 text-emerald-100 text-[10px] font-bold uppercase tracking-wider">
                <BadgeCheck className="w-3 h-3"/> MCI verified
              </span>
            </div>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              {doc.name}
            </h2>
            <div className="mt-1 text-teal-100 text-sm sm:text-base">
              {doc.specialization} · {doc.qualifications || "MBBS, MD"}
            </div>
            {doc.alma_mater && (
              <div className="mt-1 text-amber-200/90 text-sm font-semibold">
                {doc.alma_mater} alumnus
              </div>
            )}
            {doc.bio && (
              <p className="mt-3 text-teal-100/80 text-sm leading-relaxed line-clamp-3 max-w-2xl">
                {doc.bio}
              </p>
            )}
            <div className="mt-4 flex items-center gap-5 text-xs text-teal-100/90 flex-wrap">
              {doc.experience_years > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Award className="w-4 h-4"/> {doc.experience_years}+ years experience
                </span>
              )}
              {doc.rating > 0 && (
                <span className="inline-flex items-center gap-1.5 text-amber-300 font-semibold">
                  <Star className="w-4 h-4 fill-amber-300"/> {Number(doc.rating).toFixed(1)} rating
                </span>
              )}
              {typeof doc.consultations_count === "number" && doc.consultations_count > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-4 h-4"/>
                  {doc.consultations_count >= 1000
                    ? `${(doc.consultations_count / 1000).toFixed(doc.consultations_count >= 10000 ? 0 : 1)}K+ patients`
                    : `${doc.consultations_count} patients`}
                </span>
              )}
            </div>
            <div className="mt-5">
              {(() => {
                const parts = (doc.name || "").split(" ").filter(Boolean);
                // Skip honorifics so "Book with Dr." becomes "Book with Sathya"
                const first = parts.find((p) => !/^dr\.?$/i.test(p)) || "us";
                return (
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-teal-800 font-semibold text-sm shadow-lg hover:bg-teal-50 transition-colors">
                    Book with {first} <ArrowRight className="w-4 h-4"/>
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
